import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../services/apiClient";
import { useTheme } from '../config/ThemeContext';

function SeasonManagement() {
  const [seasons, setSeasons] = useState([]);
  const [seasonName, setSeasonName] = useState("");
  const [loading, setLoading] = useState(false);
  const [activatingSeason, setActivatingSeason] = useState(null);
  const [editingSeasonId, setEditingSeasonId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
  const colors = useTheme();

  useEffect(() => {
    fetchSeasons();
  }, []);

  const fetchSeasons = async () => {
    try {
      const response = await apiClient.get(`/api/seasons`);
      setSeasons(response.data);
    } catch (error) {
      console.error("Failed to fetch seasons:", error.response?.data?.error || error.message);
    }
  };


const createSeason = async () => {
  if (!seasonName) return alert("Season name is required.");
  setLoading(true);
  try {
    const response = await apiClient.post(`/api/seasons/create`, { name: seasonName });
    setSeasons((prev) => [...prev, response.data]);
    setSeasonName("");
    
    // Optional: Refresh token to include the new season
    try {
      await apiClient.post('/api/auth/refresh-token');
    } catch (refreshError) {
      console.log('Token refresh failed, but season was created');
      // This is non-critical - user might need to refresh page to see new season
    }
  } catch (error) {
    // handle error
  } finally {
    setLoading(false);
  }
};
  const setActiveSeason = async (seasonId) => {
    setActivatingSeason(seasonId);
    try {
      await apiClient.post(`/api/seasons/set-active/${seasonId}`);
      setSeasons(prevSeasons => 
        prevSeasons.map(season => ({
          ...season,
          isActive: season.id === seasonId
        }))
      );
    } catch (error) {
      console.error("Failed to set active season:", error.response?.data?.error || error.message);
      alert("Failed to set active season: " + (error.response?.data?.error || "Unknown error"));
    } finally {
      setActivatingSeason(null);
    }
  };

  const handleEditClick = (season) => {
    if (editingSeasonId === season.id) {
      setEditingSeasonId(null);
    } else {
      setEditingSeasonId(season.id);
      setEditFormData({
        name: season.name || "",
        description: season.description || "",
        rules: season.rules || "",
        bannerImageUrl: season.bannerImageUrl || "",
        logoUrl: season.logoUrl || "",
        isPublic: season.isPublic || false
      });
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const saveEditedSeason = async (seasonId) => {
    try {
      const response = await apiClient.put(`/api/seasons/update/${seasonId}`, editFormData);
      setSeasons(prevSeasons => prevSeasons.map(s => s.id === seasonId ? { ...s, ...response.data } : s));
      setEditingSeasonId(null);
    } catch (error) {
      console.error("Failed to update season:", error.response?.data?.error || error.message);
      alert("Failed to update season.");
    }
  };

  const handleNavigateToMatches = (seasonId) => {
    navigate(`/matches/setup/${seasonId}`);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Page Title */}
      <div className="text-center mb-8">
        <h1 className={`text-3xl font-bold ${colors.neutral.text} mb-2`}>
          Season Management
        </h1>
        <div className={`h-1 w-20 ${colors.primary.bg} mx-auto`}></div>
      </div>

      {/* List of Seasons */}
      <div className={`bg-white rounded-lg shadow-sm border-t-4 ${colors.neutral.border} p-6`}>
        <h2 className={`text-xl font-semibold ${colors.neutral.text} mb-6 flex items-center`}>
          <span className={`w-3 h-3 ${colors.secondary.bg} rounded-full mr-2`}></span>
          Existing Seasons
        </h2>
        
        <ul className="space-y-3">
          {seasons.map((season) => (
            <li key={season.id} className={`group ${colors.neutral.hover} transition-colors duration-150`}>
              <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-md border ${colors.neutral.border} group-hover:border-slate-200`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className={`font-medium ${colors.neutral.text}`}>{season.name}</h3>
                    {season.isActive && (
                      <span className={`px-2 py-1 text-xs font-medium rounded ${colors.state.success.bg} text-white`}>
                        Active
                      </span>
                    )}
                  </div>
                  
                </div>
                <div className="flex flex-wrap gap-2">
                  {!season.isActive && (
                    <button
                      onClick={() => setActiveSeason(season.id)}
                      disabled={activatingSeason === season.id}
                      className={colors.buttons.tertiary}
						>
                      ✔️ Set Active
                    </button>
                  )}
                  <Link
                    to={`/team-management/${season.id}`}
                    className={colors.buttons.primary}
					>
                    🧑‍🤝‍🧑Teams
                  </Link>
                  <button
                    onClick={() => handleNavigateToMatches(season.id)}
                    className={colors.buttons.primary}
					>
                    🏐 Matches
                  </button>
				  <button title="P4 Collated Results and Rankings"
				  onClick={() => window.open(`/seasons/${season.id}/team-ranking`, "_blank")}
				  className={colors.buttons.secondary}
				  >
				  🥇 P4
				</button>
				<button 
				  title="P5 Players Ranking By Skills Report"
				  onClick={() => window.open(`/seasons/${season.id}/players-ranking`, "_blank")} // 👈 Uses the secure internal route
				  className={colors.buttons.secondary}
				>
				  🏅 P5
				</button>
                  <button
                    onClick={() => handleEditClick(season)}
                    className={colors.buttons.secondary}
                  >
                    ✏️ Edit
                  </button>
                </div>
              </div>
              
              {/* Expandable Edit Form */}
              {editingSeasonId === season.id && (
                <div className={`p-4 border-t ${colors.neutral.border} bg-gray-50 rounded-b-md`}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className={`block text-sm font-medium ${colors.neutral.lightText} mb-1`}>Season Name</label>
                      <input type="text" name="name" value={editFormData.name} onChange={handleEditFormChange} className={`w-full border ${colors.neutral.border} rounded px-3 py-2`} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={`block text-sm font-medium ${colors.neutral.lightText} mb-1`}>Description</label>
                      <textarea name="description" value={editFormData.description} onChange={handleEditFormChange} rows={3} className={`w-full border ${colors.neutral.border} rounded px-3 py-2`}></textarea>
                    </div>
                    <div className="sm:col-span-2">
                      <label className={`block text-sm font-medium ${colors.neutral.lightText} mb-1`}>Rules</label>
                      <textarea name="rules" value={editFormData.rules} onChange={handleEditFormChange} rows={3} className={`w-full border ${colors.neutral.border} rounded px-3 py-2`}></textarea>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium ${colors.neutral.lightText} mb-1`}>Banner Image URL</label>
                      <input type="text" name="bannerImageUrl" value={editFormData.bannerImageUrl} onChange={handleEditFormChange} className={`w-full border ${colors.neutral.border} rounded px-3 py-2`} />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium ${colors.neutral.lightText} mb-1`}>Logo URL</label>
                      <input type="text" name="logoUrl" value={editFormData.logoUrl} onChange={handleEditFormChange} className={`w-full border ${colors.neutral.border} rounded px-3 py-2`} />
                    </div>
                    <div className="sm:col-span-2 flex items-center mt-2">
                      <input type="checkbox" id={`public-${season.id}`} name="isPublic" checked={editFormData.isPublic} onChange={handleEditFormChange} className="h-4 w-4 text-blue-600 rounded border-gray-300 mr-2" />
                      <label htmlFor={`public-${season.id}`} className={`text-sm font-medium ${colors.neutral.text}`}>Show this season in Public Fan Portal</label>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end gap-3">
                    <button onClick={() => setEditingSeasonId(null)} className={colors.buttons.tertiary}>Cancel</button>
                    <button onClick={() => saveEditedSeason(season.id)} className={colors.buttons.primary}>Save Changes</button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
	        {/* Create Season Form */}
      <div className={`bg-white rounded-lg shadow-sm border-t-4 ${colors.neutral.border} p-6 mb-8`}>
        <h2 className={`text-xl font-semibold ${colors.neutral.text} mb-4 flex items-center`}>
          <span className={`w-3 h-3 ${colors.primary.bg} rounded-full mr-2`}></span>
          Create New Season
        </h2>
        <div className="flex flex-col sm:flex-row items-end gap-4">
          <div className="flex-1 w-full">
            <label className={`block text-sm font-medium ${colors.neutral.lightText} mb-1`}>
              Season Name
            </label>
            <input
              type="text"
              placeholder="e.g. '2024 Premier League'"
              value={seasonName}
              onChange={(e) => setSeasonName(e.target.value)}
              className={`w-full border ${colors.neutral.border} rounded-md px-4 py-2.5 ${colors.neutral.text} 
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-300
                        transition-all duration-150`}
            />
          </div>
          <button
            onClick={createSeason}
            disabled={loading}
            className={colors.buttons.primary}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </span>
            ) : (
              "Create Season"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SeasonManagement;
